import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useUrlParams from '../hooks/useUrlParams';
import { SecondaryLawKind, SecondaryLawRow } from '../types/types';
import { color, font, radius, space, TAP, Tone, weight } from '../theme';
import { useBreakpoint } from '../useBreakpoint';
import { Card } from './Card';
import { Badge } from './Badge';
import Chip from './Chip';
import ListRow from './ListRow';
import SearchField from './SearchField';
import Button from './Button';
import Icon from './Icon';
import { EmptyState, ErrorState } from './States';
import { SkeletonList } from './Skeleton';

/* ── Secondary legislation: a filterable list (committee page "פעילות", ministry page) ──
   Kind chips (in the URL as `kind`), a search over names, laws and issuers, and rows of
   name, status, kind · dates · issuer (or the committee, on a ministry page) and a link to
   the document. 30 rows at a time. */

type KindFilter = 'all' | SecondaryLawKind;

const KIND_CHIPS: { key: SecondaryLawKind; label: string }[] = [
  { key: 'regulation', label: 'תקנות וצווים' },
  { key: 'report', label: 'דיווחים' },
  { key: 'action', label: 'פעולות אחרות' },
  { key: 'other', label: 'אחר' },
];

const PAGE = 30;

// The Knesset's status names → badge tone (status colors, not clickable)
const STATUS_TONE: Record<string, Tone> = {
  'לדיון בוועדה': 'info',
  'לדיון במליאה': 'info',
  'הונח על שולחן הוועדה': 'neutral',
  'הונח על שולחן הכנסת': 'neutral',
  'הושלם טיפול': 'success',
  'הושלם טיפול ברשות המבצעת': 'success',
  'פורסם ברשומות': 'success',
  'נעצר': 'danger',
};

// What the item is, as the row's first fact: the regulation's classification (תקנות, צו, …)
const kindLabel = (r: SecondaryLawRow): string =>
  r.kind === 'report' ? 'דיווח'
    : r.kind === 'action' ? 'פעולה אחרת'
      : r.classification || (r.kind === 'regulation' ? 'חקיקת משנה' : 'אחר');

// 'YYYY-MM-DD' → '13.10.2026'
const shortDate = (ymd: string): string => {
  const [y, m, d] = ymd.split('-');
  return y && m && d ? `${parseInt(d, 10)}.${parseInt(m, 10)}.${y}` : ymd;
};

// Issuers worth naming ("אחר" says nothing)
const issuers = (r: SecondaryLawRow): string[] => (r.regulators || []).filter(n => n && n !== 'אחר');

const matches = (r: SecondaryLawRow, q: string): boolean => {
  if (r.name.toLowerCase().indexOf(q) !== -1) return true;
  if (r.authorizingLaw && r.authorizingLaw.toLowerCase().indexOf(q) !== -1) return true;
  if (r.classification && r.classification.indexOf(q) !== -1) return true;
  const names = r.regulators || [];
  for (let i = 0; i < names.length; i++) if (names[i].toLowerCase().indexOf(q) !== -1) return true;
  return false;
};

interface SecondaryLawListProps {
  // The rows to list, newest received first; null while loading
  rows: SecondaryLawRow[] | null;
  error?: boolean;
  onRetry?: () => void;
  // 'committee': rows name the issuer; 'ministry': rows name the committee (a link) instead
  context: 'committee' | 'ministry';
  // Ministry context: committee doc ID → name, for the committee links
  committeeNames?: Record<string, string>;
  // The back-link label for the committee links
  from?: string;
  // Desktop: shown at the start of the search row (the committee page's switch)
  toolbarStart?: React.ReactNode;
  // A line between the chips and the list (what the list holds)
  note?: React.ReactNode;
  // When there are no rows at all
  emptyTitle: string;
  emptyBody?: React.ReactNode;
}

const SecondaryLawList = ({ rows, error, onRetry, context, committeeNames, from, toolbarStart, note, emptyTitle, emptyBody }: SecondaryLawListProps) => {
  const { isDesktop } = useBreakpoint();
  const [searchParams, setSearchParams] = useUrlParams();
  const [query, setQuery] = useState('');
  const [shown, setShown] = useState(PAGE);

  const rawKind = searchParams.get('kind');
  const kind: KindFilter = rawKind === 'regulation' || rawKind === 'report' || rawKind === 'action' || rawKind === 'other' ? rawKind : 'all';
  const setKind = (k: KindFilter) => {
    setShown(PAGE);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (k === 'all') next.delete('kind'); else next.set('kind', k);
      return next;
    }, { replace: true });
  };

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const r of rows || []) c[r.kind] = (c[r.kind] || 0) + 1;
    return c;
  }, [rows]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (rows || []).filter(r => (kind === 'all' || r.kind === kind) && (!q || matches(r, q)));
  }, [rows, kind, query]);

  const searchField = (
    <SearchField
      value={query}
      onChange={v => { setQuery(v); setShown(PAGE); }}
      placeholder="חיפוש בחקיקת המשנה"
      style={isDesktop && toolbarStart ? { width: 320, flexShrink: 0 } : undefined}
    />
  );
  // Desktop: the page's switch (at most 420px) and the search share a row; phone: one under
  // the other
  const start = toolbarStart && isDesktop ? <div style={{ flex: 1, minWidth: 0, maxWidth: 420 }}>{toolbarStart}</div> : toolbarStart;
  const toolbar = isDesktop && toolbarStart
    ? (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: space.lg }}>
        {start}
        {searchField}
      </div>
    )
    : <>{toolbarStart}{searchField}</>;

  if (error) {
    return <>{start}<Card padding={0}><ErrorState onRetry={onRetry || (() => undefined)} /></Card></>;
  }
  if (rows === null) return <>{start}<SkeletonList rows={6} /></>;
  if (rows.length === 0) {
    return <>{start}<Card padding={0}><EmptyState icon="document" title={emptyTitle} body={emptyBody} /></Card></>;
  }

  const kindChips = KIND_CHIPS.filter(c => (counts[c.key] || 0) > 0);

  return (
    <>
      {toolbar}
      {/* Kind chips wrap onto a second line rather than scroll */}
      <div role="group" aria-label="סוג" style={{ display: 'flex', flexWrap: 'wrap', columnGap: space.sm }}>
        <Chip selected={kind === 'all'} onClick={() => setKind('all')} count={rows.length}>הכל</Chip>
        {kindChips.map(c => (
          <Chip key={c.key} selected={kind === c.key} onClick={() => setKind(kind === c.key ? 'all' : c.key)} count={counts[c.key]}>{c.label}</Chip>
        ))}
      </div>
      {note && <div style={{ fontSize: font.small, color: color.textSecondary, lineHeight: 1.5 }}>{note}</div>}

      {filtered.length === 0
        ? (
          <Card padding={0}>
            <EmptyState
              title="לא נמצאו תוצאות"
              action={<Button variant="ghost" onClick={() => { setQuery(''); setKind('all'); }}>ניקוי הסינון</Button>}
            />
          </Card>
        )
        : (
          <Card padding={0}>
            {filtered.slice(0, shown).map((r, i) => {
              const facts: React.ReactNode[] = [kindLabel(r)];
              if (context === 'ministry' && r.committeeId && committeeNames && committeeNames[r.committeeId]) {
                facts.push(
                  <Link key="c" to={`/committees/${r.committeeId}`} state={from ? { from } : undefined} style={{ color: color.primary, fontWeight: weight.medium, textDecoration: 'none' }}>
                    {committeeNames[r.committeeId]}
                  </Link>,
                );
              }
              if (r.received) facts.push(`התקבל ${shortDate(r.received)}`);
              if (r.approved && r.kind !== 'report') facts.push(`אושר ${shortDate(r.approved)}`);
              if (context === 'committee') {
                const names = issuers(r);
                if (names.length > 0) facts.push(names.join(', '));
              }
              return (
                <ListRow
                  key={r.id}
                  divider={i > 0}
                  title={
                    <span title={r.name} style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {r.name}
                    </span>
                  }
                  subtitle={
                    <span style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: `${space.xs}px ${space.xs + 2}px`, marginTop: space.xs }}>
                      {r.status && <Badge tone={STATUS_TONE[r.status] || 'neutral'}>{r.status}</Badge>}
                      {r.isEmergency && <Badge tone="warning">שעת חירום</Badge>}
                      <span>
                        {facts.map((f, j) => <React.Fragment key={j}>{j > 0 && ' · '}{f}</React.Fragment>)}
                      </span>
                    </span>
                  }
                  trailing={r.docUrl
                    ? (
                      // A 44px target around the 36px button, aligned with the title's first line
                      <a
                        href={r.docUrl}
                        target="_blank"
                        rel="noreferrer"
                        aria-label="המסמך"
                        title="המסמך"
                        style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', justifyContent: 'center', width: TAP, height: TAP, margin: -(TAP - 36) / 2, flexShrink: 0 }}
                      >
                        <span style={{ width: 36, height: 36, borderRadius: radius.md, background: color.primarySoft, color: color.primary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Icon name="document" size={18} strokeWidth={2} />
                        </span>
                      </a>
                    )
                    : undefined}
                />
              );
            })}
          </Card>
        )}
      {shown < filtered.length && (
        <Button fullWidth onClick={() => setShown(n => n + PAGE)}>הצגת עוד ({(filtered.length - shown).toLocaleString('he-IL')} נותרו)</Button>
      )}
    </>
  );
};

export default SecondaryLawList;
